import React, { useState } from "react";
import { Form, Formik } from "formik";
import moment from "moment-timezone";
import { Box, Flex, Grid } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import SelectSearch from "components/forms/SelectSearch";
import SelectDate from "components/forms/SelectDate";
import GooglePlaces from "components/forms/GooglePlaces";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";
import { useToast } from "components/toast";

import { createWorkOrder, getServicePlans, getPlanGroups, getInstantEstimateProducts } from "api/ServicePlans";
import { getClients } from "api/Clients";
import { getLocations, createLocation, getMapsAtLocation } from "api/Locations";

import type { EnrichedSite } from "./planRosterTypes";

/**
 * Session 56 (PLAN_WO_INHERITANCE.md) — resolve the plan's default
 * product + service type for a new work order from its
 * serviceTypesConfig (the row flagged defaultOnNewWo, else the first
 * row). `type` holds a product id; `serviceId` the service type, which
 * may be null ("decide at the work order").
 */
function defaultsFromServiceTypes(
  cfg: any,
): { productId: string | null; serviceId: string | null } {
  const rows = Array.isArray(cfg) ? cfg : [];
  if (rows.length === 0) return { productId: null, serviceId: null };
  const row = rows.find((r: any) => r?.defaultOnNewWo) ?? rows[0];
  return {
    productId: row?.type || null,
    serviceId: row?.serviceId || null,
  };
}

/**
 * v3.4 — create a Work Order manually. Two modes, one modal:
 *
 * PLAN CONTEXT (planId supplied — Plan Detail header). Unchanged from
 * the original: Location picker scoped to Locations already on this
 * plan, WO born with servicePlanId + lcpId set.
 *
 * STANDALONE (no planId — the WO Queue's "+ New Work Order", Session
 * 46, ALIGNMENT_REVIEW.md Misalignment 3). This is the manual arm of
 * intake, sibling to the email pipeline, and it is not gated by a
 * plan:
 *   • Client is REQUIRED. The Client is what resolves the LCP, so it
 *     is the one field that can't be skipped.
 *   • Location is an existing pick OR a new address entered inline
 *     (Google Places). A new address runs through the dedup-checked
 *     POST /locations findOrCreate, then the backend pairs
 *     (location, client) into an LCP via findOrCreate. A Location
 *     JSCRM has never seen is one step, not a setup ceremony.
 *   • Plan is OPTIONAL, pre-filled when the client has exactly one
 *     open plan. An untagged WO is a valid one-off; the queue renders
 *     it as not-in-a-plan and staff can attach it later.
 *   • Batch is OPTIONAL, shown only when the chosen plan has batches.
 *
 * sourceType is hardcoded to `manual_upload` for both modes. External
 * ID is optional, used when the client provided a Corrigo / other-
 * system reference so the audit trail matches on both sides.
 */
export default function NewWorkOrderModal({
  planId,
  planName,
  clientId,
  sites,
  sitesLoading,
  preselectedLcpId,
  planServiceTypes,
  onClose,
  onCreated,
}: {
  planId?: string;
  planName?: string;
  /** Plan-context only — the plan's client, needed to resolve an LCP
   * when a new address is entered in the moment (findOrCreate pairs
   * the new Location with this client, and the backend validates the
   * plan belongs to this client). */
  clientId?: string;
  sites?: EnrichedSite[];
  /** True while the plan's Location list is still being fetched. The
   * list is loaded on demand (it is unpaged and large on an
   * EMCOR-shaped plan), so the picker must say "loading" rather than
   * render an empty option list that reads as "no Locations". */
  sitesLoading?: boolean;
  /** Plan-context only — seed the Location picker when the modal is
   * opened from a specific Location row's expanded panel. */
  preselectedLcpId?: string;
  /** Plan-context only (Session 56, PLAN_WO_INHERITANCE.md) — the plan's
   * serviceTypesConfig, so a new work order pre-fills the plan's default
   * product + service type. Standalone mode reads the chosen plan's
   * config from the client's plan list instead. */
  planServiceTypes?: any[] | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Instant-Estimate products — the primary pricing input. Each product
  // now carries the SERVICE TYPES it can be sold as (W-q), resolved from
  // the same source the estimate engine prices from.
  const [products, setProducts] = useState<any[] | null>(null);

  // W-p — the site map's calculated total for the chosen location, and
  // whether the operator has taken control of the quantity field. Once
  // they type, the map never overwrites again.
  const [mapSuggestion, setMapSuggestion] = useState<
    { name: string | null; totalCuYd: number } | null
  >(null);
  const [quantityTouched, setQuantityTouched] = useState(false);

  React.useEffect(() => {
    let alive = true;
    getInstantEstimateProducts().then((rows: any[]) => {
      if (alive) setProducts(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const planContext = !!planId;

  // Session 56 (PLAN_WO_INHERITANCE.md) — in plan context, the new work
  // order inherits the plan's default product + service type. Editable;
  // the operator can override for this work order.
  const planCtxDefaults = planContext
    ? defaultsFromServiceTypes(planServiceTypes)
    : { productId: null as string | null, serviceId: null as string | null };

  // Standalone mode: plans + batches for the picked client / plan.
  // null = not loaded yet (or no client picked); [] = loaded, none.
  const [clientPlans, setClientPlans] = useState<any[] | null>(null);
  const [planGroups, setPlanGroups] = useState<Record<string, any[]>>({});

  const locationOptions = (sites || [])
    .filter((s) => !!s.lcpId)
    .map((s) => ({
      value: s.lcpId,
      label: [s.lcpName || s.address?.line1 || "(Unnamed Location)", s.address?.city, s.address?.state]
        .filter(Boolean)
        .join(" · "),
    }));

  /** KNOWN_ISSUES #12 — the intake stamp, previously hardcoded to
   * `manual_upload` here and uneditable afterward, so a WO raised by hand
   * for a job that IS a Corrigo job could never be labelled as one. Same
   * enum as the entity column and both DTOs. Manual leads because it is
   * the right answer for almost every WO raised through this modal. */
  const sourceOptions = [
    { value: "manual_upload", label: "Manual" },
    { value: "email_corrigo", label: "CorrigoPro" },
    { value: "email_generic", label: "Email" },
    { value: "scheduled", label: "Recurring" },
  ];

  const fetchGroupsFor = async (targetPlanId: string) => {
    if (!targetPlanId || planGroups[targetPlanId]) return;
    const rows = await getPlanGroups({ data: { planId: targetPlanId } });
    setPlanGroups((prev) => ({ ...prev, [targetPlanId]: Array.isArray(rows) ? rows : [] }));
  };

  /**
   * Manual validate rather than a Yup schema: the required set shifts
   * with mode and locationMode, and expressing that as Yup `.when`
   * chains is harder to read than the checks themselves.
   */
  const validate = (values: any) => {
    const errors: any = {};

    if (planContext) {
      // Plan context reuses the same existing/new location toggle as
      // standalone. Existing = pick an LCP already on the plan; new =
      // enter an address in the moment (findOrCreate against the plan's
      // client). A preselected lcp (opened from a Location row) forces
      // existing mode implicitly.
      if (values.locationMode === "new") {
        if (!values.newLocation?.latitude || !values.newLocation?.longitude) {
          errors.newLocation = "Search and select an address.";
        }
      } else {
        if (!values.lcpId) errors.lcpId = "Pick a Location.";
      }
    } else {
      if (!values.clientId) errors.clientId = "Pick a client.";
      if (values.locationMode === "existing") {
        if (!values.locationId) errors.locationId = "Pick a Location.";
      } else {
        if (!values.newLocation?.latitude || !values.newLocation?.longitude) {
          errors.newLocation = "Search and select an address.";
        }
      }
    }

    if (!values.problemDescription?.trim()) {
      // Notes are optional now — no error.
    }
    if (!values.receivedAt) errors.receivedAt = "When did this come in?";

    // A12 (Session 57) — on a work_order_fed plan a WO must carry a
    // number OR a name. Client-side check is best-effort: it fires only
    // when we can see the chosen plan's intakeStyle (standalone mode,
    // from the loaded client plans). The backend enforces the same rule
    // authoritatively for every path, so plan-context intake (where the
    // style isn't in hand here) still can't slip a blank identity
    // through — it just surfaces server-side instead.
    const chosenPlanForValidation = (clientPlans || []).find(
      (p: any) => p.id === values.servicePlanId,
    );
    if (chosenPlanForValidation?.intakeStyle === "work_order_fed") {
      const hasNumber = !!values.externalId?.trim();
      const hasName = !!values.name?.trim();
      if (!hasNumber && !hasName) {
        errors.externalId = "Enter a work order number or a name.";
      }
    }
    if (values.quantity !== "" && values.quantity != null && Number(values.quantity) < 0) {
      errors.quantity = "Must be zero or more.";
    }
    if (values.nteAmount !== "" && values.nteAmount != null && Number(values.nteAmount) < 0) {
      errors.nteAmount = "Must be zero or more.";
    }

    return errors;
  };

  const submit = async (values: any) => {
    setSubmitting(true);

    const payload: any = {
      sourceType: values.sourceType || "manual_upload",
      problemDescription: values.problemDescription,
      receivedAt: values.receivedAt
        ? moment.unix(values.receivedAt).toISOString()
        : new Date().toISOString(),
    };

    if (values.productId) payload.productId = values.productId;
    if (values.serviceId) payload.serviceId = values.serviceId;
    if (values.quantity !== "" && values.quantity != null) {
      payload.quantity = Number(values.quantity);
    }

    if (planContext) {
      payload.servicePlanId = planId;
      if (values.locationMode === "new") {
        // New address in plan context: findOrCreate the Location, then
        // send clientId + locationId (no lcpId). The backend resolves
        // the LCP against the plan's client and attaches it to the
        // plan, validating the plan belongs to that client.
        const loc = await createLocation({
          name: values.newLocationName?.trim() || undefined,
          latitude: values.newLocation.latitude,
          longitude: values.newLocation.longitude,
          address: values.newLocation.address || {},
        });
        if (!loc?.id) {
          setSubmitting(false);
          toast.add(
            loc?.message || "Error creating the Location.",
            "var(--theme-ui-colors-danger)",
          );
          return;
        }
        if (!clientId) {
          setSubmitting(false);
          toast.add(
            "Missing the plan's client; cannot attach a new address.",
            "var(--theme-ui-colors-danger)",
          );
          return;
        }
        payload.clientId = clientId;
        payload.locationId = loc.id;
      } else {
        payload.lcpId = values.lcpId;
      }
    } else {
      // New address: findOrCreate the Location first. The backend
      // dedup-checks by coords then address line1, so a repeat entry
      // of a known place resolves to the existing row.
      let locationId = values.locationId;
      if (values.locationMode === "new") {
        const loc = await createLocation({
          name: values.newLocationName?.trim() || undefined,
          latitude: values.newLocation.latitude,
          longitude: values.newLocation.longitude,
          address: values.newLocation.address || {},
        });
        if (!loc?.id) {
          setSubmitting(false);
          toast.add(
            loc?.message || "Error creating the Location.",
            "var(--theme-ui-colors-danger)",
          );
          return;
        }
        locationId = loc.id;
      }
      payload.clientId = values.clientId;
      payload.locationId = locationId;
      if (values.servicePlanId) payload.servicePlanId = values.servicePlanId;
      if (values.groupId) payload.groupId = values.groupId;
    }

    if (values.priority?.trim()) payload.priority = values.priority.trim();
    if (values.externalId?.trim()) payload.externalId = values.externalId.trim();
    if (values.name?.trim()) payload.name = values.name.trim();
    if (values.acceptRejectByAt)
      payload.acceptRejectByAt = moment.unix(values.acceptRejectByAt).toISOString();
    if (values.onSiteByAt) payload.onSiteByAt = moment.unix(values.onSiteByAt).toISOString();
    if (values.completeByAt) payload.completeByAt = moment.unix(values.completeByAt).toISOString();
    if (values.nteAmount !== "" && values.nteAmount != null) {
      payload.nteAmount = Number(values.nteAmount);
    }

    const res = await createWorkOrder({ data: payload });
    setSubmitting(false);

    if (res?.id) {
      toast.add("Work order created.", "var(--theme-ui-colors-success)");
      onCreated();
    } else {
      toast.add(
        res?.message || "Error creating work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Plan context with zero Locations no longer hard-blocks: the
  // new-address path can create + attach a Location in the moment. Only
  // block when opened from a specific Location row (preselected) that
  // somehow has no options — an inconsistent state we don't want to act
  // on. Otherwise the toggle handles the empty plan.
  const planContextEmpty =
    planContext && !!preselectedLcpId && !sitesLoading && locationOptions.length === 0;

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>
        {planContext ? `New Work Order — ${planName}` : "New Work Order"}
      </ModalHeader>
      <Formik
        initialValues={{
          // plan-context field
          lcpId: preselectedLcpId || "",
          // standalone fields
          clientId: "",
          locationMode:
            planContext && !preselectedLcpId && locationOptions.length === 0
              ? "new"
              : "existing",
          locationId: "",
          newLocation: null as any,
          newLocationName: "",
          servicePlanId: "",
          groupId: "",
          // pricing inputs (primary)
          productId: planCtxDefaults.productId || "",
          serviceId: planCtxDefaults.serviceId || "",
          quantity: "",
          // shared fields
          sourceType: "manual_upload",
          problemDescription: "",
          priority: "",
          externalId: "",
          name: "",
          receivedAt: moment().unix(),
          acceptRejectByAt: undefined,
          onSiteByAt: undefined,
          completeByAt: undefined,
          nteAmount: "",
        }}
        validate={validate}
        onSubmit={submit}
      >
        {({ values, errors, submitCount, setFieldValue }) => {
          const selectedPlanGroups = values.servicePlanId
            ? planGroups[values.servicePlanId]
            : undefined;

          // W-q — the service types the chosen product can be sold as.
          // Empty until a product is picked, because "what services are
          // available" is only answerable per product.
          const servicesForProduct: Array<{ id: string; name: string }> =
            (products || []).find((p: any) => p.id === values.productId)?.services ?? [];

          // W-p — which Location the operator has landed on, whichever
          // route they took to it. In plan context they pick an LCP from
          // the roster (which carries locationId); standalone, they pick
          // a Location directly.
          const resolvedLocationId: string | null =
            values.locationId ||
            (values.lcpId
              ? (sites || []).find((s: any) => s.lcpId === values.lcpId)?.locationId ?? null
              : null);

          // Session 56 (PLAN_WO_INHERITANCE.md) — pre-fill product +
          // service type from a chosen plan's default row, unless the
          // operator has already typed a product (never clobber intent).
          const maybeApplyPlanDefaults = (plan: any) => {
            if (values.productId) return;
            const d = defaultsFromServiceTypes(plan?.serviceTypesConfig);
            if (d.productId) {
              setFieldValue("productId", d.productId);
              setFieldValue("serviceId", d.serviceId || "");
            }
          };

          const onClientChange = async (c: any) => {
            setFieldValue("clientId", c?.id || "");
            setFieldValue("servicePlanId", "");
            setFieldValue("groupId", "");
            setClientPlans(null);
            if (!c?.id) return;

            const res = await getServicePlans({
              query: {
                filters: { clientId: c.id },
                recordsPer: 100,
                sortBy: "updatedAt",
                sortDir: "desc",
              },
            });
            const items = Array.isArray(res?.items) ? res.items : [];
            const eligible = items.filter(
              (p: any) => (p.effectiveStatus ?? p.status) !== "closed",
            );
            setClientPlans(eligible);

            // Pre-fill when the client has exactly one open plan. The
            // plan stays a visible, clearable field either way.
            if (eligible.length === 1) {
              setFieldValue("servicePlanId", eligible[0].id);
              fetchGroupsFor(eligible[0].id);
              maybeApplyPlanDefaults(eligible[0]);
            }
          };

          const onPlanChange = (v: any) => {
            setFieldValue("servicePlanId", v || "");
            setFieldValue("groupId", "");
            if (v) {
              fetchGroupsFor(v);
              const chosen = (clientPlans || []).find((p: any) => p.id === v);
              if (chosen) maybeApplyPlanDefaults(chosen);
            }
          };

          return (
            <Form noValidate autoComplete="off">
              <ModalBody>
                {planContextEmpty ? (
                  <Box>
                    <Subtext
                      fontSize={"13px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginBottom={"8px"}
                      color={themevals.colors.dkgrey}
                    >
                      This plan has no Locations yet. Add a Location to the plan first, then create a work order against it.
                    </Subtext>
                  </Box>
                ) : (
                  <>
                    {planContext ? (
                      preselectedLcpId ? (
                        // Opened from a specific Location row — that
                        // Location is the intent; keep it simple.
                        <SelectInput
                          name="lcpId"
                          label="Location"
                          required
                          value={values.lcpId}
                          options={locationOptions}
                          onChange={(v: any) => setFieldValue("lcpId", v || "")}
                          searchable={locationOptions.length > 8}
                          clearable={false}
                          $errors={errors.lcpId && submitCount > 0 ? errors.lcpId : null}
                        />
                      ) : (
                        <>
                          <Flex sx={{ gap: "8px", marginBottom: "8px" }}>
                            <ModeChip
                              label="Plan Location"
                              active={values.locationMode !== "new"}
                              onClick={() => setFieldValue("locationMode", "existing")}
                            />
                            <ModeChip
                              label="New address"
                              active={values.locationMode === "new"}
                              onClick={() => setFieldValue("locationMode", "new")}
                            />
                          </Flex>

                          {values.locationMode === "new" ? (
                            <>
                              <GooglePlaces
                                name="newLocation"
                                label="Address"
                                required
                                placeholder="Search for the address..."
                                onChange={(v: any) => setFieldValue("newLocation", v || null)}
                                $errors={
                                  errors.newLocation && submitCount > 0
                                    ? (errors.newLocation as string)
                                    : null
                                }
                              />
                              <Box sx={{ marginTop: "12px" }}>
                                <TextInput
                                  name="newLocationName"
                                  label="Location name"
                                  value={values.newLocationName}
                                  onChange={(val: any) => setFieldValue("newLocationName", val)}
                                  placeholder="Optional, e.g. The Goddard School #4127"
                                />
                              </Box>
                              <Subtext
                                fontSize={"11px"}
                                fontFamily={"Poppins"}
                                margin={0}
                                marginTop={"6px"}
                                color={themevals.colors.dkgrey}
                              >
                                The new address is paired with this plan&apos;s client and added to the plan. If it already exists as a Location, we reuse it instead of creating a duplicate.
                              </Subtext>
                            </>
                          ) : (
                            <SelectInput
                              name="lcpId"
                              label="Location"
                              required
                              value={values.lcpId}
                              options={locationOptions}
                              onChange={(v: any) => setFieldValue("lcpId", v || "")}
                              searchable={locationOptions.length > 8}
                              clearable={false}
                              $errors={errors.lcpId && submitCount > 0 ? errors.lcpId : null}
                            />
                          )}
                        </>
                      )
                    ) : (
                      <>
                        <SelectSearch
                          name="clientId"
                          label="Client"
                          required
                          api={getClients}
                          value={values.clientId}
                          keyValue="id"
                          keyLabel={["name"]}
                          sortBy="name"
                          sortDir="ASC"
                          onChange={onClientChange}
                          $errors={
                            errors.clientId && submitCount > 0
                              ? (errors.clientId as string)
                              : null
                          }
                          placeholder="Search clients..."
                        />

                        <Box sx={{ marginTop: "16px" }}>
                          <Flex sx={{ gap: "8px", marginBottom: "8px" }}>
                            <ModeChip
                              label="Existing Location"
                              active={values.locationMode === "existing"}
                              onClick={() => setFieldValue("locationMode", "existing")}
                            />
                            <ModeChip
                              label="New address"
                              active={values.locationMode === "new"}
                              onClick={() => setFieldValue("locationMode", "new")}
                            />
                          </Flex>

                          {values.locationMode === "existing" ? (
                            <SelectSearch
                              name="locationId"
                              label="Location"
                              required
                              api={getLocations}
                              value={values.locationId}
                              keyValue="id"
                              keyLabel={["name", "address.line1", "address.city", "address.state"]}
                              labelDivider=" · "
                              sortBy="createdAt"
                              sortDir="DESC"
                              preload={!!values.clientId}
                              params={
                                values.clientId
                                  ? { filters: { associatedClientId: values.clientId } }
                                  : {}
                              }
                              onChange={(loc: any) =>
                                setFieldValue("locationId", loc?.id || "")
                              }
                              $errors={
                                errors.locationId && submitCount > 0
                                  ? (errors.locationId as string)
                                  : null
                              }
                              placeholder={
                                values.clientId
                                  ? "This client's Locations..."
                                  : "Pick a client first, or use New address"
                              }
                            />
                          ) : (
                            <>
                              <GooglePlaces
                                name="newLocation"
                                label="Address"
                                required
                                placeholder="Search for the address..."
                                onChange={(v: any) => setFieldValue("newLocation", v || null)}
                                $errors={
                                  errors.newLocation && submitCount > 0
                                    ? (errors.newLocation as string)
                                    : null
                                }
                              />
                              <Box sx={{ marginTop: "12px" }}>
                                <TextInput
                                  name="newLocationName"
                                  label="Location name"
                                  value={values.newLocationName}
                                  onChange={(val: any) => setFieldValue("newLocationName", val)}
                                  placeholder="Optional, e.g. The Goddard School #4127"
                                />
                              </Box>
                              <Subtext
                                fontSize={"11px"}
                                fontFamily={"Poppins"}
                                margin={0}
                                marginTop={"6px"}
                                color={themevals.colors.dkgrey}
                              >
                                If this address already exists as a Location, we reuse it instead of creating a duplicate.
                              </Subtext>
                            </>
                          )}
                        </Box>

                        {values.clientId && (
                          <Box sx={{ marginTop: "16px" }}>
                            <SelectInput
                              name="servicePlanId"
                              label="Maintenance Plan"
                              value={values.servicePlanId}
                              options={[
                                { value: "", label: "No plan (one-off)" },
                                ...(clientPlans || []).map((p: any) => ({
                                  value: p.id,
                                  label:
                                    (p.effectiveStatus ?? p.status) === "closing"
                                      ? `${p.name} (closing)`
                                      : p.name,
                                })),
                              ]}
                              onChange={onPlanChange}
                              searchable={false}
                              clearable={false}
                            />
                            {clientPlans !== null && clientPlans.length === 0 && (
                              <Subtext
                                fontSize={"11px"}
                                fontFamily={"Poppins"}
                                margin={0}
                                marginTop={"6px"}
                                color={themevals.colors.dkgrey}
                              >
                                This client has no open Maintenance Plans. The work order will be a one-off.
                              </Subtext>
                            )}
                          </Box>
                        )}

                        {values.servicePlanId &&
                          Array.isArray(selectedPlanGroups) &&
                          selectedPlanGroups.length > 0 && (
                            <Box sx={{ marginTop: "16px" }}>
                              <SelectInput
                                name="groupId"
                                label="Batch"
                                value={values.groupId}
                                options={[
                                  { value: "", label: "No batch" },
                                  ...selectedPlanGroups.map((g: any) => ({
                                    value: g.id,
                                    label: g.name,
                                  })),
                                ]}
                                onChange={(v: any) => setFieldValue("groupId", v || "")}
                                searchable={false}
                                clearable={false}
                              />
                            </Box>
                          )}
                      </>
                    )}

                    {planContext && sitesLoading && (
                      <Subtext
                        fontSize={"11px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginTop={"6px"}
                        color={themevals.colors.dkgrey}
                      >
                        Loading this plan&apos;s Locations…
                      </Subtext>
                    )}

                    {/* W-p — watches the chosen Location and offers the
                        site map's calculated total as the quantity. Its
                        own component so the lookup can be an effect
                        inside Formik's render prop. */}
                    <MapQuantityWatcher
                      locationId={resolvedLocationId}
                      currentQuantity={values.quantity}
                      quantityTouched={quantityTouched}
                      onSuggestion={setMapSuggestion}
                      onFill={(yards: number) => setFieldValue("quantity", String(yards))}
                    />

                    <Grid gap={3} columns={[1, "2fr 2fr 1fr"]} sx={{ marginTop: "16px" }}>
                      <SelectInput
                        name="productId"
                        label="Product"
                        value={values.productId}
                        options={[
                          { value: "", label: "Choose a product..." },
                          ...(products || []).map((p: any) => ({ value: p.id, label: p.name })),
                        ]}
                        onChange={(v: any) => {
                          setFieldValue("productId", v || "");
                          // The service list is per product, so a product
                          // change invalidates any service already picked.
                          setFieldValue("serviceId", "");
                        }}
                        searchable={(products?.length ?? 0) > 8}
                        clearable={false}
                      />
                      {/* W-q — the SERVICE TYPE. Optional: when set, the
                          generated RFQ is priced for this service alone
                          instead of carrying every option. Only the
                          services the chosen product can actually be sold
                          as are listed, because they come from the same
                          source the estimate engine prices from. */}
                      <SelectInput
                        name="serviceId"
                        label="Service type"
                        value={values.serviceId}
                        options={[
                          {
                            value: "",
                            label: values.productId
                              ? "Decide later — price both"
                              : "Choose a product first",
                          },
                          ...servicesForProduct.map((s: any) => ({
                            value: s.id,
                            label: s.name,
                          })),
                        ]}
                        onChange={(v: any) => setFieldValue("serviceId", v || "")}
                        searchable={false}
                        clearable={false}
                        disabled={!values.productId || servicesForProduct.length === 0}
                      />
                      <TextInput
                        type="number"
                        name="quantity"
                        label="Quantity"
                        min={0}
                        value={values.quantity}
                        onChange={(val: any) => {
                          setFieldValue("quantity", val);
                          // Once the operator touches it, the map must
                          // never overwrite it again.
                          setQuantityTouched(true);
                        }}
                        $errors={errors.quantity && submitCount > 0 ? errors.quantity : null}
                        placeholder="e.g. yards"
                      />
                    </Grid>
                    {/* W-p — say where a prefilled quantity came from. A
                        number that appears on its own is a number nobody
                        trusts. */}
                    {mapSuggestion && (
                      <Subtext
                        fontSize={"11px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginTop={"6px"}
                        color={mnt.green}
                      >
                        Quantity {quantityTouched ? "suggested" : "filled"} from the site map
                        {mapSuggestion.name ? ` “${mapSuggestion.name}”` : ""} ·{" "}
                        {mapSuggestion.totalCuYd} yd³. Adjust it if the map does not match the
                        work.
                      </Subtext>
                    )}
                    {(() => {
                      const cfg = planContext
                        ? planServiceTypes
                        : (clientPlans || []).find(
                            (p: any) => p.id === values.servicePlanId,
                          )?.serviceTypesConfig;
                      const d = defaultsFromServiceTypes(cfg);
                      const show = !!d.productId && values.productId === d.productId;
                      return show ? (
                        <Subtext
                          fontSize={"11px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          marginTop={"6px"}
                          color={mnt.green}
                        >
                          Pre-filled from this plan&apos;s defaults. Change the product or service type for this work order if it differs.
                        </Subtext>
                      ) : null;
                    })()}
                    <Subtext
                      fontSize={"11px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginTop={"6px"}
                      color={themevals.colors.dkgrey}
                    >
                      Product and quantity are what price the RFQ. Quantity can be added later, but a quantity greater than zero is required to generate the RFQ. Leaving the service type unset prices every option so you can choose once you see the numbers.
                    </Subtext>

                    <Box sx={{ marginTop: "16px" }}>
                      <TextInput
                        type="textarea"
                        name="problemDescription"
                        label="Notes"
                        rows={3}
                        value={values.problemDescription}
                        onChange={(val: any) => setFieldValue("problemDescription", val)}
                        placeholder="Optional. Anything the client noted, or context for the crew."
                      />
                    </Box>
                    <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                      <TextInput
                        name="externalId"
                        label="Work order number"
                        value={values.externalId}
                        onChange={(val: any) => setFieldValue("externalId", val)}
                        placeholder="e.g. Corrigo WO #"
                        $errors={
                          errors.externalId && submitCount > 0
                            ? (errors.externalId as string)
                            : null
                        }
                      />
                      <TextInput
                        name="name"
                        label="Work order name"
                        value={values.name}
                        onChange={(val: any) => setFieldValue("name", val)}
                        placeholder="e.g. Mulch install, north playground"
                      />
                    </Grid>
                    {/* A12 — a WO reads by its number or its name. On a
                        work_order_fed plan at least one is required (the
                        number leads, the name is the descriptor); on
                        other plans both are optional. */}
                    <Subtext
                      fontSize={"11px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginTop={"6px"}
                      color={themevals.colors.dkgrey}
                    >
                      The number is how this work order is looked up; the name describes the job. On a work-order-fed plan, enter at least one.
                    </Subtext>
                    {/* KNOWN_ISSUES #12 — priority was a fixed 5-option
                        select (low/medium/high/urgent) here while the
                        importer writes whatever the client's system sends
                        and the WO edit form is free text. Production had
                        two disjoint vocabularies as a result: 40 imported
                        LCG WOs read "P30 Routine - 20 day ETA, 30 day
                        Comp", the hand-raised ones read "urgent" / "low",
                        and nothing could ever be filtered across both. Now
                        free text, matching the edit form exactly, so a
                        manually raised WO can carry the vocabulary the
                        plan actually uses. */}
                    <Box sx={{ marginTop: "16px" }}>
                      <TextInput
                        name="priority"
                        label="Priority"
                        value={values.priority}
                        onChange={(val: any) => setFieldValue("priority", val)}
                        placeholder="e.g. P30 Routine, Urgent"
                      />
                    </Box>
                    <Box sx={{ marginTop: "16px" }}>
                      <SelectInput
                        name="sourceType"
                        label="Source"
                        value={values.sourceType}
                        options={sourceOptions}
                        onChange={(v: any) => setFieldValue("sourceType", v || "manual_upload")}
                        searchable={false}
                        clearable={false}
                      />
                    </Box>
                    <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                      <SelectDate
                        name="receivedAt"
                        label="Received"
                        required
                        usePortal
                        value={values.receivedAt}
                        onChange={(val: any) => setFieldValue("receivedAt", val)}
                        $errors={
                          errors.receivedAt && submitCount > 0 ? errors.receivedAt : null
                        }
                      />
                      <SelectDate
                        name="acceptRejectByAt"
                        label="Accept by"
                        usePortal
                        value={values.acceptRejectByAt}
                        onChange={(val: any) => setFieldValue("acceptRejectByAt", val)}
                      />
                    </Grid>
                    <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                      <SelectDate
                        name="onSiteByAt"
                        label="On-site by"
                        usePortal
                        value={values.onSiteByAt}
                        onChange={(val: any) => setFieldValue("onSiteByAt", val)}
                      />
                      <SelectDate
                        name="completeByAt"
                        label="Complete by"
                        usePortal
                        value={values.completeByAt}
                        onChange={(val: any) => setFieldValue("completeByAt", val)}
                      />
                    </Grid>
                    <Box sx={{ marginTop: "16px" }}>
                      <TextInput
                        type="number"
                        name="nteAmount"
                        label="NTE (Not To Exceed) amount"
                        min={0}
                        value={values.nteAmount}
                        onChange={(val: any) => setFieldValue("nteAmount", val)}
                        $errors={
                          errors.nteAmount && submitCount > 0 ? errors.nteAmount : null
                        }
                        placeholder="Optional"
                      />
                    </Box>
                    <Subtext
                      fontSize={"11px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginTop={"12px"}
                      color={themevals.colors.dkgrey}
                    >
                      {planContext || values.servicePlanId
                        ? 'This work order will land in the WO Queue as "Manually matched" against the picked Location. Generate an RFQ for it from the queue when you are ready.'
                        : "This work order is not tagged to a plan. It lands in the WO Queue as a one-off; you can attach it to a plan later from the queue."}
                    </Subtext>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <BasicButton
                  type="button"
                  outline
                  scheme="secondary"
                  onClick={onClose}
                >
                  Cancel
                </BasicButton>
                {!planContextEmpty && (
                  <BasicButton
                    type="submit"
                    $submitting={submitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!submitting && <CheckMarkIcon fill={"#fff"} />}
                    Create Work Order
                  </BasicButton>
                )}
              </ModalFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

/**
 * Small segmented-mode chip for the existing-vs-new Location toggle.
 * Plain <button> rather than theme-ui Box: BoxProps carries neither
 * `type` nor `disabled`, and inside a Formik <Form> a typeless button
 * submits on click. Same precedent as the WO Queue's batch chip.
 */
function ModeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "1px solid",
        borderColor: active ? mnt.ink : mnt.divider,
        background: active ? mnt.ink : mnt.cardBg,
        color: active ? mnt.cardBg : mnt.ink,
        borderRadius: "14px",
        padding: "4px 14px",
        fontSize: "12px",
        fontFamily: "Poppins",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/**
 * W-p (WO_SERVICE_AND_QUANTITY.md) — offers the site map's calculated
 * total as the work order's quantity.
 *
 * Jack's rule: when a location already has a map, the quantity should
 * come from it, but the internal team must be able to override "even if
 * the map isn't matching perfectly". So this FILLS an untouched, empty
 * field and never does anything else — it will not overwrite a number
 * the operator typed, and it will not silently correct one they edited.
 * The label next to the field always says where the number came from,
 * because a quantity that appears on its own is a quantity nobody
 * trusts.
 *
 * Renders nothing. It exists as a component so the lookup can be an
 * effect while living inside Formik's render prop, where the selected
 * location is known.
 *
 * `getMapsAtLocation` returns one row per map at the location, ordered
 * by `updatedAt DESC`, each carrying `totalCuYd` computed by the
 * backend. The most recently touched map wins; we do NOT sum across
 * maps, because separate maps at one site are usually alternative
 * proposals, not additive areas.
 */
function MapQuantityWatcher({
  locationId,
  currentQuantity,
  quantityTouched,
  onSuggestion,
  onFill,
}: {
  locationId: string | null;
  currentQuantity: any;
  quantityTouched: boolean;
  onSuggestion: (s: { name: string | null; totalCuYd: number } | null) => void;
  onFill: (yards: number) => void;
}) {
  const lastLookupRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    if (!locationId) {
      onSuggestion(null);
      lastLookupRef.current = null;
      return;
    }
    // One lookup per location, not per keystroke elsewhere in the form.
    if (lastLookupRef.current === locationId) return;
    lastLookupRef.current = locationId;

    getMapsAtLocation(locationId).then((maps) => {
      if (!alive) return;
      const best = (maps || []).find((m: any) => Number(m?.totalCuYd) > 0);
      if (!best) {
        onSuggestion(null);
        return;
      }
      const yards = Number(best.totalCuYd);
      onSuggestion({ name: best.name ?? null, totalCuYd: yards });

      const isEmpty =
        currentQuantity === "" || currentQuantity == null || Number(currentQuantity) === 0;
      if (isEmpty && !quantityTouched) onFill(yards);
    });

    return () => {
      alive = false;
    };
    // Location-keyed only: this must NOT re-run when the quantity or the
    // touched flag changes, or typing would retrigger the lookup. The
    // bare disable matches the rest of the codebase — the react-hooks
    // plugin is not registered in this project's eslint config, so a
    // rule-specific disable is itself an error.
    // eslint-disable-next-line
  }, [locationId]);

  return null;
}
